/**
 * Tech Director Dashboard Data Service
 *
 * 100% REAL DATA with RESILIENT FETCHING
 *
 * Data sources:
 * - Employee data: workforce API (getMyAllReports)
 * - Financial/client data: monumenta SIG API (getMyClients)
 * - Vacation data: workforce vacation API (getVacationAnalytics, getVacationCalendar)
 * - Performance data: performance API (getManagerDashboardSummary, getClimateTrends)
 * - Calendar data: workforce calendar API + performance 1-on-1s
 * - Budget data: monumenta budgets API (getTrainingBudget) - company-wide training budget
 *
 * Resilience features:
 * - Retry with exponential backoff for transient network errors
 * - Graceful degradation: partial data display when some sources fail
 * - Caching: stale data displayed while retrying
 */

import { getMyClients } from '@/api/sig-data'
import {
  getMyAllReports,
  getVacationAnalytics,
  getVacationCalendar,
  getMyTeamEvents
} from '@/api/workforce'
import {
  getManagerDashboardSummary,
  getClimateTrends,
  getMyOneOnOnes
} from '@/api/performance'
import { getTrainingBudget } from '@/api/budgets'
import { getTenantDomain } from '@/lib/services/api-utils'
import { withRetry, getCached, setCache, type ResilientResult } from '@/lib/resilient-fetch'
import type { Employee } from '@/types/workforce'
import type { ClientResultApiResponse, MyClientResponse } from '@/types/financial'

// ============================================================================
// TYPES
// ============================================================================

export interface DashboardData {
  priorityActions: PriorityAction[]
  keyMetrics: KeyMetrics
  categoryCards: CategoryCard[]
  rawData: {
    employees: Employee[]
    clients: (ClientResultApiResponse | MyClientResponse)[]
  }
  /** Data sources that failed to load (for partial data display) */
  failedSources?: string[]
  /** Whether this data came from cache */
  fromCache?: boolean
}

export interface PriorityAction {
  id: string
  type: 'vacation' | 'feedback' | 'budget' | 'evaluation' | 'balance'
  severity: 'critical' | 'warning' | 'info'
  title: string
  description: string
  count?: number
  link: string
  createdAt: string // ISO 8601 timestamp
}

export interface KeyMetrics {
  results: {
    totalRevenue: number
    totalTarget: number
    performance: number
    clientCount: number
  }
  team: {
    totalEmployees: number
    activeEmployees: number
    onVacation: number
    contractBreakdown: {
      CLT: number
      PJ: number
      SOCIO: number
    }
  }
  performance: {
    teamAverage: number
    climateSatisfaction: number
    trend: 'up' | 'down' | 'stable'
  }
  budget: {
    totalBudget: number
    usedBudget: number
    availableBudget: number
    utilizationPercentage: number
  }
}

export interface CategoryCard {
  id: string
  title: string
  icon: string
  preview: string
  stats: string[]
  links: {
    label: string
    path: string
    badge?: number
  }[]
  updatedAt: string // ISO 8601 timestamp
}

// ============================================================================
// REAL DATA FETCHERS
// ============================================================================

/**
 * Fetch REAL employee data
 */
async function fetchEmployeeData(): Promise<Employee[]> {
  const response = await getMyAllReports({ limit: 100 })
  return response.items || []
}

/**
 * Fetch REAL financial/client data
 */
async function fetchFinancialData(): Promise<(ClientResultApiResponse | MyClientResponse)[]> {
  const year = new Date().getFullYear().toString()
  const clients = await getMyClients(year)
  return Array.isArray(clients) ? clients : []
}

/**
 * Fetch REAL vacation data
 */
async function fetchVacationData() {
  const today = new Date()
  const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
  const twoWeeksFromNow = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000)

  const startDateStr = oneWeekAgo.toISOString().split('T')[0]
  const endDateStr = twoWeeksFromNow.toISOString().split('T')[0]

  const [analytics, calendar] = await Promise.all([
    getVacationAnalytics({
      startDate: startDateStr,
      endDate: endDateStr
    }),
    getVacationCalendar({
      startDate: startDateStr,
      endDate: endDateStr
    })
  ])

  // Count currently on vacation (approved and currently active)
  const onVacation = calendar.filter(event => {
    if (event.status !== 'approved') return false
    const start = new Date(event.startDate)
    const end = new Date(event.endDate)
    return start <= today && end >= today
  }).length

  return {
    pendingApprovals: analytics.pendingApprovals,
    criticalBalances: analytics.criticalBalances,
    onVacation,
    expiringDays: analytics.totalDaysRemaining // Use actual expiring days from API
  }
}

/**
 * Fetch REAL performance data
 */
async function fetchPerformanceData() {
  const tenantDomain = getTenantDomain()

  const [dashboardSummary, climateTrends] = await Promise.all([
    getManagerDashboardSummary(tenantDomain),
    getClimateTrends(tenantDomain, { period: 12 })
  ])

  // Calculate trend from climate data
  const trends = climateTrends.trendData || []
  let trend: 'up' | 'down' | 'stable' = 'stable'

  // Use the first category's trend data if available
  if (trends.length > 0 && trends[0].dataPoints.length >= 2) {
    const dataPoints = trends[0].dataPoints
    const latest = dataPoints[dataPoints.length - 1]?.value || 0
    const previous = dataPoints[dataPoints.length - 2]?.value || 0
    if (latest > previous + 0.2) trend = 'up'
    else if (latest < previous - 0.2) trend = 'down'
  }

  // Calculate climate satisfaction from improving/declining categories
  const improvingCount = climateTrends.summary.improving.length
  const decliningCount = climateTrends.summary.declining.length
  const climateSatisfaction = improvingCount > decliningCount ? 1 : decliningCount > improvingCount ? -1 : 0

  return {
    teamAverage: Math.round(dashboardSummary.data.performanceMetrics.teamAverage || 0),
    climateSatisfaction,
    trend,
    pendingEvaluations: dashboardSummary.data.feedbackStats.pending || 0,
    pendingFeedbacks: dashboardSummary.data.feedbackStats.pending || 0
  }
}

/**
 * Fetch REAL calendar data (team events + 1-on-1s)
 */
async function fetchCalendarData() {
  const today = new Date()
  const oneWeekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
  const tenantDomain = getTenantDomain()

  const [teamEvents, oneOnOnes] = await Promise.all([
    getMyTeamEvents({
      startDate: today.toISOString().split('T')[0],
      endDate: oneWeekFromNow.toISOString().split('T')[0],
      limit: 50
    }),
    getMyOneOnOnes(tenantDomain, {
      upcoming: true,
      status: 'scheduled',
      limit: 20
    })
  ])

  return {
    upcomingMeetings: teamEvents.length + (oneOnOnes.items?.length || 0)
  }
}

/**
 * Fetch REAL budget data from Monumenta budgets API
 * Returns company-wide training budget
 */
async function fetchBudgetData() {
  const currentYear = new Date().getFullYear()
  const budget = await getTrainingBudget(currentYear)

  // Handle case where budget doesn't exist yet
  if (!budget) {
    return {
      totalBudget: 0,
      usedBudget: 0,
      availableBudget: 0,
      utilizationPercentage: 0,
      coursesPlanned: 0,
      coursesCompleted: 0,
      coursesInProgress: 0,
      teamCompletionRate: 0
    }
  }

  // Map Monumenta TrainingBudget to dashboard format
  const availableBudget = budget.budget - budget.actualCost

  return {
    totalBudget: budget.budget,
    usedBudget: budget.actualCost,
    availableBudget,
    utilizationPercentage: budget.utilizationPercent,
    // Monumenta budget doesn't track individual courses
    // These would need a separate endpoint if course tracking is required
    coursesPlanned: 0,
    coursesCompleted: 0,
    coursesInProgress: 0,
    teamCompletionRate: 0
  }
}

// ============================================================================
// DATA PROCESSORS
// ============================================================================

/**
 * Process real employee data into metrics
 */
function processEmployeeData(employees: Employee[]) {
  const activeEmployees = employees.filter(e => e.status === 'ACTIVE')

  const contractBreakdown = activeEmployees.reduce((acc, emp) => {
    const type = emp.currentEngagement?.contractType || 'UNKNOWN'
    acc[type] = (acc[type] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  return {
    totalEmployees: employees.length,
    activeEmployees: activeEmployees.length,
    contractBreakdown: {
      CLT: contractBreakdown['CLT'] || 0,
      PJ: contractBreakdown['PJ'] || 0,
      SOCIO: contractBreakdown['SOCIO'] || 0
    }
  }
}

/**
 * Process real financial data into metrics
 * Handles both admin and director response formats
 */
function processFinancialData(clients: (ClientResultApiResponse | MyClientResponse)[]) {
  let totalRevenue = 0
  let totalTarget = 0

  clients.forEach((client) => {
    // Both formats now have receitas and proa at top level
    totalRevenue += client.receitas || 0
    totalTarget += client.proa || 0
  })

  const performance = totalTarget > 0 ? (totalRevenue / totalTarget) * 100 : 0

  return {
    totalRevenue,
    totalTarget,
    performance: Math.round(performance * 10) / 10, // Round to 1 decimal
    clientCount: clients.length,
  }
}

/**
 * Build priority actions from real data
 */
function buildPriorityActions(
  vacationData: Awaited<ReturnType<typeof fetchVacationData>>,
  performanceData: Awaited<ReturnType<typeof fetchPerformanceData>>,
  budgetData: Awaited<ReturnType<typeof fetchBudgetData>>
): PriorityAction[] {
  const actions: PriorityAction[] = []
  const now = new Date().toISOString()

  // Vacation approvals (critical if > 3)
  if (vacationData.pendingApprovals > 0) {
    actions.push({
      id: 'vacation-approvals',
      type: 'vacation',
      severity: vacationData.pendingApprovals > 3 ? 'critical' : 'warning',
      title: 'Solicitações de Férias Pendentes',
      description: `${vacationData.pendingApprovals} solicitaç${vacationData.pendingApprovals > 1 ? 'ões' : 'ão'} aguardando aprovação`,
      count: vacationData.pendingApprovals,
      link: '/vacation',
      createdAt: now
    })
  }

  // Critical vacation balances (always critical if > 0)
  if (vacationData.criticalBalances > 0) {
    actions.push({
      id: 'critical-balances',
      type: 'balance',
      severity: 'critical',
      title: 'Saldos de Férias com Urgência',
      description: `${vacationData.criticalBalances} colaborador${vacationData.criticalBalances > 1 ? 'es' : ''} com mais de 30 dias acumulados ou próximo do vencimento`,
      count: vacationData.criticalBalances,
      link: '/vacation',
      createdAt: now
    })
  }

  // Pending feedbacks (warning if > 2)
  if (performanceData.pendingFeedbacks > 2) {
    actions.push({
      id: 'pending-feedbacks',
      type: 'feedback',
      severity: 'warning',
      title: 'Feedbacks Pendentes',
      description: `${performanceData.pendingFeedbacks} feedback${performanceData.pendingFeedbacks > 1 ? 's' : ''} requerendo sua atenção`,
      count: performanceData.pendingFeedbacks,
      link: '/performance/feedbacks',
      createdAt: now
    })
  }

  // Pending evaluations (warning if > 2)
  if (performanceData.pendingEvaluations > 2) {
    actions.push({
      id: 'pending-evaluations',
      type: 'evaluation',
      severity: 'warning',
      title: 'Avaliações Pendentes',
      description: `${performanceData.pendingEvaluations} avaliaç${performanceData.pendingEvaluations > 1 ? 'ões' : 'ão'} de desempenho prevista${performanceData.pendingEvaluations > 1 ? 's' : ''} para este mês`,
      count: performanceData.pendingEvaluations,
      link: '/performance/avaliacoes',
      createdAt: now
    })
  }

  // Budget warning (if < 20% remaining)
  const budgetRemainingPercent = 100 - budgetData.utilizationPercentage
  if (budgetRemainingPercent < 20 && budgetData.totalBudget > 0) {
    actions.push({
      id: 'budget-low',
      type: 'budget',
      severity: 'warning',
      title: 'Orçamento de Capacitação Baixo',
      description: `Restam apenas ${budgetRemainingPercent.toFixed(0)}% (R$ ${(budgetData.availableBudget / 1000).toFixed(0)}k) para o trimestre`,
      link: '/available-budget',
      createdAt: now
    })
  }

  // Sort by severity
  const severityOrder = { critical: 0, warning: 1, info: 2 }
  return actions.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity])
}

/**
 * Build category cards with links and previews
 */
function buildCategoryCards(
  results: KeyMetrics['results'],
  team: KeyMetrics['team'] & { onVacation: number },
  performanceData: Awaited<ReturnType<typeof fetchPerformanceData>>,
  budgetData: Awaited<ReturnType<typeof fetchBudgetData>>,
  upcomingMeetings: number
): CategoryCard[] {
  const now = new Date().toISOString()

  return [
    {
      id: 'resultados',
      title: 'Resultados',
      icon: '',
      preview: `${results.performance.toFixed(0)}% da meta alcançada`,
      stats: [
        `R$ ${(results.totalRevenue / 1000000).toFixed(1)}M realizados`,
        `${results.clientCount} cliente${results.clientCount !== 1 ? 's' : ''} ativo${results.clientCount !== 1 ? 's' : ''}`
      ],
      links: [
        { label: 'Ver Resultados Completos', path: '/results' },
        { label: 'Minhas Metas de Negócio', path: '/my-business-targets' }
      ],
      updatedAt: now
    },
    {
      id: 'desempenho',
      title: 'Desempenho',
      icon: '',
      preview: `Média ${performanceData.teamAverage}, clima ${performanceData.climateSatisfaction.toFixed(1)}/10`,
      stats: [
        `${performanceData.pendingEvaluations} avaliaç${performanceData.pendingEvaluations !== 1 ? 'ões' : 'ão'} pendente${performanceData.pendingEvaluations !== 1 ? 's' : ''}`,
        `${performanceData.pendingFeedbacks} feedback${performanceData.pendingFeedbacks !== 1 ? 's' : ''} para revisar`
      ],
      links: [
        { label: 'Visão Geral de Desempenho', path: '/performance' },
        { label: 'Avaliações', path: '/performance/avaliacoes', badge: performanceData.pendingEvaluations },
        { label: 'Feedbacks', path: '/performance/feedbacks', badge: performanceData.pendingFeedbacks },
        { label: 'Pesquisa de Clima', path: '/performance/clima' }
      ],
      updatedAt: now
    },
    {
      id: 'equipe',
      title: 'Equipe',
      icon: '',
      preview: `${team.totalEmployees} colaboradores, ${team.onVacation} de férias`,
      stats: [
        `${team.activeEmployees} ativos (${team.contractBreakdown.CLT} CLT, ${team.contractBreakdown.PJ} PJ)`,
        `${upcomingMeetings} reuniã${upcomingMeetings !== 1 ? 'ões' : 'o'} esta semana`
      ],
      links: [
        { label: 'Todos Colaboradores', path: '/employees' },
        { label: 'Gestão de Férias', path: '/vacation', badge: team.onVacation },
        { label: 'Calendário da Equipe', path: '/calendar' },
        { label: 'Onboarding', path: '/onboarding' }
      ],
      updatedAt: now
    },
    {
      id: 'capacitacao',
      title: 'Capacitação',
      icon: '',
      preview: `${budgetData.utilizationPercentage.toFixed(0)}% do orçamento usado`,
      stats: [
        `R$ ${(budgetData.totalBudget / 1000).toFixed(0)}k orçamento total`,
        `R$ ${(budgetData.availableBudget / 1000).toFixed(0)}k disponível`
      ],
      links: [
        { label: 'Orçamento Disponível', path: '/available-budget' },
        { label: 'Meus Cursos', path: '/my-courses' },
        { label: 'Cursos da Equipe', path: '/team-courses' }
      ],
      updatedAt: now
    }
  ]
}

// ============================================================================
// CACHE KEYS
// ============================================================================

const CACHE_KEY = 'tech-director-dashboard'
const CACHE_MAX_AGE_MS = 5 * 60 * 1000 // 5 minutes

// ============================================================================
// DEFAULT VALUES FOR GRACEFUL DEGRADATION
// ============================================================================

const defaultVacationData = {
  pendingApprovals: 0,
  criticalBalances: 0,
  onVacation: 0,
  expiringDays: 0
}

const defaultPerformanceData = {
  teamAverage: 0,
  climateSatisfaction: 0,
  trend: 'stable' as const,
  pendingEvaluations: 0,
  pendingFeedbacks: 0
}

const defaultCalendarData = {
  upcomingMeetings: 0
}

const defaultBudgetData = {
  totalBudget: 0,
  usedBudget: 0,
  availableBudget: 0,
  utilizationPercentage: 0,
  coursesPlanned: 0,
  coursesCompleted: 0,
  coursesInProgress: 0,
  teamCompletionRate: 0
}

// ============================================================================
// RESILIENT FETCH WRAPPER
// ============================================================================

type FetchResult<T> = ResilientResult<T>

async function resilientFetch<T>(
  name: string,
  fetcher: () => Promise<T>,
  defaultValue: T
): Promise<{ data: T; failed: boolean }> {
  try {
    const data = await withRetry(fetcher, { maxRetries: 2, initialDelayMs: 500 })
    return { data, failed: false }
  } catch (error) {
    console.error(`[Dashboard] Failed to fetch ${name}:`, error)
    return { data: defaultValue, failed: true }
  }
}

// ============================================================================
// MAIN EXPORT
// ============================================================================

/**
 * Fetch and process all dashboard data with resilience
 *
 * Features:
 * - Retry with exponential backoff for transient errors
 * - Graceful degradation: shows partial data when some sources fail
 * - Returns cached data if all fetches fail
 */
export async function fetchTechDirectorDashboard(): Promise<DashboardData> {
  // Try to get cached data first (used as fallback if everything fails)
  const cachedData = getCached<DashboardData>(CACHE_KEY, CACHE_MAX_AGE_MS)

  // Fetch all data in parallel with individual resilience
  const [
    employeesResult,
    clientsResult,
    vacationResult,
    performanceResult,
    calendarResult,
    budgetResult
  ] = await Promise.all([
    resilientFetch('employees', fetchEmployeeData, []),
    resilientFetch('clients', fetchFinancialData, []),
    resilientFetch('vacation', fetchVacationData, defaultVacationData),
    resilientFetch('performance', fetchPerformanceData, defaultPerformanceData),
    resilientFetch('calendar', fetchCalendarData, defaultCalendarData),
    resilientFetch('budget', fetchBudgetData, defaultBudgetData)
  ])

  // Track which sources failed
  const failedSources: string[] = []
  if (employeesResult.failed) failedSources.push('employees')
  if (clientsResult.failed) failedSources.push('clients')
  if (vacationResult.failed) failedSources.push('vacation')
  if (performanceResult.failed) failedSources.push('performance')
  if (calendarResult.failed) failedSources.push('calendar')
  if (budgetResult.failed) failedSources.push('budget')

  // If critical data sources failed and we have cache, return cached data
  const criticalSourcesFailed = employeesResult.failed && clientsResult.failed
  if (criticalSourcesFailed && cachedData) {
    console.warn('[Dashboard] Critical sources failed, returning cached data')
    return { ...cachedData, fromCache: true, failedSources }
  }

  // If ALL sources failed and no cache, throw error
  if (failedSources.length === 6) {
    throw new Error('Unable to load dashboard data. Please check your network connection.')
  }

  // Extract data
  const employees = employeesResult.data
  const clients = clientsResult.data
  const vacationData = vacationResult.data
  const performanceData = performanceResult.data
  const calendarData = calendarResult.data
  const budgetData = budgetResult.data

  // Process data
  const teamData = processEmployeeData(employees)
  const resultsData = processFinancialData(clients)

  // Build dashboard sections
  const priorityActions = buildPriorityActions(vacationData, performanceData, budgetData)

  const keyMetrics: KeyMetrics = {
    results: resultsData,
    team: {
      ...teamData,
      onVacation: vacationData.onVacation
    },
    performance: {
      teamAverage: performanceData.teamAverage,
      climateSatisfaction: performanceData.climateSatisfaction,
      trend: performanceData.trend
    },
    budget: budgetData
  }

  const categoryCards = buildCategoryCards(
    resultsData,
    { ...teamData, onVacation: vacationData.onVacation },
    performanceData,
    budgetData,
    calendarData.upcomingMeetings
  )

  const dashboardData: DashboardData = {
    priorityActions,
    keyMetrics,
    categoryCards,
    rawData: {
      employees,
      clients
    },
    failedSources: failedSources.length > 0 ? failedSources : undefined
  }

  // Cache successful data
  setCache(CACHE_KEY, dashboardData)

  return dashboardData
}

/**
 * Format currency for display
 * Uses Brazilian Real (BRL) formatting with decimals
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)
}

/**
 * Format large numbers with K/M suffix
 */
export function formatLargeNumber(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}K`
  }
  return value.toFixed(0)
}
